import { useState } from "react";
import AdminCard from "./AdminCard";

interface Props { onSave: () => void; }

export default function AdminHero({ onSave }: Props) {
  const [badge, setBadge] = useState("Section 21 FSMA 2000 — Regulated Financial Promotion");
  const [headline, setHeadline] = useState("Social Housing");
  const [headlineAccent, setHeadlineAccent] = useState("Structured Product");
  const [subtext, setSubtext] = useState("A 5-year, income-generating investment backed by UK assisted living assets — targeting 10.00% per annum, distributed monthly.");
  const [disclaimer, setDisclaimer] = useState("Intended solely for Certified High Net Worth Individuals, Self-Certified Sophisticated Investors, or eligible retail investors. Investments involve risk. Capital may be at risk.");
  const [cta1Label, setCta1Label] = useState("Explore the Product");
  const [cta1Link, setCta1Link] = useState("/social-housing");
  const [cta2Label, setCta2Label] = useState("Schedule a Call");
  const [statReturn, setStatReturn] = useState("10.00% p.a.");
  const [statTerm, setStatTerm] = useState("5 Years");
  const [statListing, setStatListing] = useState("NASDAQ Dubai Bonds");
  const [tags, setTags] = useState(["10.00% p.a. Target", "Monthly Income", "5-Year Term", "UK Social Housing", "NASDAQ Dubai Bonds"]);

  const [ctaBanner, setCtaBanner] = useState({
    badge: "Structured Investment — Qualified Investors Only",
    headline: "Ready to Invest in",
    headlineAccent: "UK Social Housing?",
    subtext: "Explore the full product documentation or speak directly with our investment team.",
    disclaimer: "Capital is at risk. Please read the full Information Memorandum before investing.",
    cta1Label: "View Full Product Details",
    cta1Link: "/social-housing",
    cta2Label: "Download Brochure",
    cta2Link: "/brochure",
    cta3Label: "Speak to Our Team",
    cta3Link: "/contact",
  });

  const updateTag = (i: number, v: string) => setTags((prev) => prev.map((t, idx) => idx === i ? v : t));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>Hero Section</h1>
        <p className="text-slate-500 text-sm mt-1">Edit the main homepage hero banner — headline, subtext, CTAs, floating stat cards, and key terms tags.</p>
      </div>

      <AdminCard title="Regulatory Badge">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Badge Text</label>
          <input value={badge} onChange={(e) => setBadge(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
        </div>
      </AdminCard>

      <AdminCard title="Headline">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Line 1 (White)</label>
            <input value={headline} onChange={(e) => setHeadline(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Line 2 (Gold Accent)</label>
            <input value={headlineAccent} onChange={(e) => setHeadlineAccent(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
        </div>
      </AdminCard>

      <AdminCard title="Body Text">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Main Subtext</label>
            <textarea value={subtext} onChange={(e) => setSubtext(e.target.value)} rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Disclaimer (small text below subtext)</label>
            <textarea value={disclaimer} onChange={(e) => setDisclaimer(e.target.value)} rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
          </div>
        </div>
      </AdminCard>

      <AdminCard title="CTA Buttons">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Primary Button Label</label>
            <input value={cta1Label} onChange={(e) => setCta1Label(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Primary Button Link</label>
            <input value={cta1Link} onChange={(e) => setCta1Link(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] font-mono" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Secondary Button Label</label>
            <input value={cta2Label} onChange={(e) => setCta2Label(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
        </div>
      </AdminCard>

      <AdminCard title="Floating Stat Cards (Desktop Only)">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Card 1 — Target Return</label>
            <input value={statReturn} onChange={(e) => setStatReturn(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Card 2 — Investment Term</label>
            <input value={statTerm} onChange={(e) => setStatTerm(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Card 3 — Also Available</label>
            <input value={statListing} onChange={(e) => setStatListing(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
        </div>
      </AdminCard>

      <AdminCard title="Key Terms Strip (5 Tags)">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tags.map((tag, i) => (
            <div key={i}>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Tag {i + 1}</label>
              <input value={tag} onChange={(e) => updateTag(i, e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
            </div>
          ))}
        </div>
      </AdminCard>

      <AdminCard title="Bottom CTA Banner">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Badge Text</label>
              <input value={ctaBanner.badge} onChange={(e) => setCtaBanner({ ...ctaBanner, badge: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Headline (Line 1)</label>
              <input value={ctaBanner.headline} onChange={(e) => setCtaBanner({ ...ctaBanner, headline: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Headline Accent (Gold)</label>
              <input value={ctaBanner.headlineAccent} onChange={(e) => setCtaBanner({ ...ctaBanner, headlineAccent: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Subtext</label>
              <input value={ctaBanner.subtext} onChange={(e) => setCtaBanner({ ...ctaBanner, subtext: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Disclaimer</label>
            <input value={ctaBanner.disclaimer} onChange={(e) => setCtaBanner({ ...ctaBanner, disclaimer: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { key: "cta1Label", label: "Button 1 Label" },
              { key: "cta2Label", label: "Button 2 Label" },
              { key: "cta3Label", label: "Button 3 Label" },
            ].map((b) => (
              <div key={b.key}>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">{b.label}</label>
                <input value={ctaBanner[b.key as keyof typeof ctaBanner]} onChange={(e) => setCtaBanner({ ...ctaBanner, [b.key]: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
              </div>
            ))}
          </div>
        </div>
      </AdminCard>

      <div className="flex justify-end">
        <button onClick={onSave} className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#071A2E] text-white text-sm font-semibold hover:bg-[#0D2B4A] transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-save-line leading-none" />
          Save Changes
        </button>
      </div>
    </div>
  );
}
